'use client';

import * as React from 'react';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { SustainabilityGovernanceVisionCardData } from '@/types/sustainability';

type NullableVisionCard = SustainabilityGovernanceVisionCardData | null | undefined;

interface SustainabilitySocialVisionSectionProps {
  cmsHeading?: string | null;
  cmsSubheadingHtml?: string | null;
  cmsLeftCard?: SustainabilityGovernanceVisionCardData | null;
  cmsRightCard?: SustainabilityGovernanceVisionCardData | null;
}

type VisionItem = {
  id: string;
  heading?: string;
  descriptionHtml: string;
};

type VisionCard = {
  id: string;
  title: string;
  items: VisionItem[];
  light?: boolean;
};

function mapCardItems(card: NullableVisionCard, prefix: string): VisionItem[] {
  return (
    card?.bullets
      ?.filter((b) => b.heading?.trim() || b.descriptionHtml?.trim())
      .map((b, i) => ({
        id: `${prefix}-cms-${i}`,
        heading: b.heading?.trim() || undefined,
        descriptionHtml: b.descriptionHtml ?? '',
      })) ?? []
  );
}

function buildVisionCards(
  cmsLeftCard: NullableVisionCard,
  cmsRightCard: NullableVisionCard,
  leftItems: VisionItem[],
  rightItems: VisionItem[],
): VisionCard[] {
  const cards: VisionCard[] = [];
  if (cmsLeftCard?.title?.trim() || leftItems.length > 0) {
    cards.push({
      id: 'milestones',
      title: cmsLeftCard?.title?.trim() ?? '',
      items: leftItems,
    });
  }
  if (cmsRightCard?.title?.trim() || rightItems.length > 0) {
    cards.push({
      id: 'standards',
      title: cmsRightCard?.title?.trim() ?? '',
      items: rightItems,
      light: true,
    });
  }
  return cards;
}

/** A vision bullet: optional bold heading followed by inline HTML description. */
function VisionItemText({ item, light }: { item: VisionItem; light?: boolean }) {
  return (
    <p
      className={`text-[13px] leading-relaxed sm:text-[14px] [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold ${
        light ? 'text-black/85 [&_strong]:text-black' : 'text-white/85 [&_strong]:text-white'
      }`}
    >
      {item.heading ? (
        <strong className={`font-semibold ${light ? 'text-black' : 'text-white'}`}>
          {item.heading}
        </strong>
      ) : null}{' '}
      <span dangerouslySetInnerHTML={{ __html: item.descriptionHtml }} />
    </p>
  );
}

/** One vision card (dark "milestones" or light "standards" variant). */
const VisionCardArticle = React.forwardRef<HTMLDivElement, { card: VisionCard }>(
  function VisionCardArticle({ card }, ref) {
    const light = Boolean(card.light);
    return (
      <article
        ref={ref}
        className={
          light
            ? 'rounded-xl border border-dashed border-black/20 bg-[#efefef] px-4 py-5 text-black sm:px-6 sm:py-7'
            : 'rounded-xl border border-black/20 bg-black px-4 py-5 text-white shadow-[0_6px_20px_rgba(0,0,0,0.15)] sm:px-6 sm:py-7'
        }
      >
        {card.title ? (
          <h3
            className={`text-[1.5rem] font-semibold leading-[1.1] sm:text-[1.75rem] md:text-[2rem] ${
              light ? 'text-black' : 'text-white'
            }`}
          >
            {card.title}
          </h3>
        ) : null}
        <ul className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
          {card.items.map((item) => (
            <li key={item.id} className="flex items-start gap-3">
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                  light ? 'bg-black' : 'bg-white'
                }`}
              >
                <Image
                  src="/images/double-check.svg"
                  alt=""
                  width={12}
                  height={12}
                  className={light ? 'invert' : undefined}
                  aria-hidden
                />
              </span>
              <VisionItemText item={item} light={light} />
            </li>
          ))}
        </ul>
      </article>
    );
  },
);

export default function SustainabilitySocialVisionSection({
  cmsHeading,
  cmsSubheadingHtml,
  cmsLeftCard,
  cmsRightCard,
}: SustainabilitySocialVisionSectionProps) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const pinRef = React.useRef<HTMLDivElement>(null);
  const leftCardRef = React.useRef<HTMLDivElement>(null);
  const rightCardRef = React.useRef<HTMLDivElement>(null);

  const heading = cmsHeading?.trim() ?? '';
  const subheadingHtml = cmsSubheadingHtml?.trim() ?? '';

  const leftItems = mapCardItems(cmsLeftCard, 'm');
  const rightItems = mapCardItems(cmsRightCard, 's');

  const cards: VisionCard[] = buildVisionCards(cmsLeftCard, cmsRightCard, leftItems, rightItems);

  const hasContent =
    Boolean(heading) || Boolean(subheadingHtml) || cards.some((card) => card.items.length > 0);

  React.useLayoutEffect(() => {
    if (!hasContent) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    gsap.registerPlugin(ScrollTrigger);

    const trigger = sectionRef.current;
    const pinEl = pinRef.current;
    if (!trigger || !pinEl) return;

    const ctx = gsap.context(() => {
      const animatedEls = [leftCardRef.current, rightCardRef.current].filter(
        (el): el is HTMLDivElement => Boolean(el),
      );

      if (animatedEls.length === 0) return;

      animatedEls.forEach((el, index) => {
        gsap.set(el, { y: index === 0 ? 120 : -120, autoAlpha: 0 });
      });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger,
          start: 'top top',
          end: '+=90%',
          pin: pinEl,
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      animatedEls.forEach((el, index) => {
        timeline.to(el, { y: 0, autoAlpha: 1, duration: 0.45, ease: 'power2.out' }, 0);
      });
    }, trigger);

    return () => {
      ctx.revert();
    };
  }, [hasContent]);

  if (!hasContent) return null;

  const leftCard = cards.find((card) => card.id === 'milestones');
  const rightCard = cards.find((card) => card.id === 'standards');

  return (
    <section ref={sectionRef} className="relative bg-[#efefef] text-[#111]">
      <div ref={pinRef} className="flex items-center lg:min-h-[100svh]">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 sm:px-8 sm:py-12 md:py-14 lg:px-10">
          <div className="mx-auto max-w-4xl text-center">
            {heading ? (
              <h2 className="text-[1.85rem] font-semibold leading-[1.1] tracking-tight text-black sm:text-[2.4rem] md:text-[2.85rem] lg:text-[3.45rem]">
                {heading}
              </h2>
            ) : null}
            {subheadingHtml ? (
              <div
                className="mx-auto mt-3 max-w-[50rem] text-[12px] leading-relaxed text-black/80 sm:mt-4 sm:text-[13px] [&_p]:m-0 [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: subheadingHtml }}
              />
            ) : null}
          </div>

          {leftCard || rightCard ? (
            <div className="mt-8 grid gap-4 sm:mt-10 lg:grid-cols-2 lg:gap-5">
              {leftCard ? <VisionCardArticle ref={leftCardRef} card={leftCard} /> : null}
              {rightCard ? <VisionCardArticle ref={rightCardRef} card={rightCard} /> : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
